import { lineDrawing, type Drawing } from './picture-tiles'
import { clear, cycleLabel, cycleOptions, el } from './ui'

// How often, one question everywhere (Liam, 27 Oct 2026, design D: "I like having an initial question that unlocks ...
// on B the weeks is in the question so the numbers are simpler ... we do it like that through the app";
// https://claude.ai/artifact/QohsHwWVMeh2Hu24X6wkWP, Astra's check). First "What kind of customer?" as picture tiles:
// Regular, Just once, Ad hoc (When they ask). Regular then asks "Every how many weeks?" with plain numbers 2, 4, 6, 8 and
// Other (12 weeks, 6 months, yearly, the round's monthly or years, or any number of weeks). Saved as before: cycle_days,
// or cycle_text's marker ("Just once", "Ad-hoc"); nothing picked is Not set, shown as no tile chosen, never guessed.

export interface Often { days: number | null; marker: string | null }
export type OftenKind = 'regular' | 'once' | 'adhoc'

const QUICK = [14, 28, 42, 56]
const KIND: Record<OftenKind, { title: string; sub: string; drawing: Drawing; marker: string | null }> = {
  regular: { title: 'Regular', sub: 'Every few weeks', drawing: 'again', marker: null },
  once: { title: 'Just once', sub: 'A one-off', drawing: 'tick', marker: 'Just once' },
  adhoc: { title: 'Ad hoc', sub: 'When they ask', drawing: 'phone', marker: 'Ad-hoc' },
}

// Which tile a saved answer is: a number of days is Regular; Just once its own; When they ask and every old no-regular
// spelling (One off, Ad hoc, Not wanted) Ad hoc; blank or TBC none (Not set).
export function oftenKind(v: Often | null): OftenKind | null {
  if (!v) return null
  if (v.days && v.days > 0) return 'regular'
  const label = cycleLabel(null, v.marker)
  if (label === 'just once') return 'once'
  if (label === 'when they ask') return 'adhoc'
  return null
}

export interface OftenPicker {
  node: HTMLElement
  // The answer as it stands: null while Regular has no number yet, or nothing is picked.
  value: () => Often | null
  kind: () => OftenKind | null
}

// `kinds`: the tiles this place offers (an extra job has Regular and Just once, "no repeat"). `onChange` runs on every
// tap with the answer so far (null while incomplete), so a place that saves at once waits for a whole answer.
export function howOftenPicker(o: { value: Often | null; kinds?: OftenKind[]; question?: string; onChange: (v: Often | null, kind: OftenKind | null) => void }): OftenPicker {
  const kinds = o.kinds ?? ['regular', 'once', 'adhoc']
  let kind: OftenKind | null = oftenKind(o.value)
  let days: number | null = kind === 'regular' ? o.value!.days : null
  let otherOpen = days !== null && !QUICK.includes(days)
  const node = el('div', { class: 'often-picker' })
  const value = (): Often | null => kind === null ? null : kind === 'regular' ? (days ? { days, marker: null } : null) : { days: null, marker: KIND[kind].marker }
  const changed = (): void => { draw(); o.onChange(value(), kind) }

  const tile = (k: OftenKind): HTMLElement => {
    const on = kind === k
    const b = el('button', { type: 'button', class: `wd-extra-tile often-kind${on ? ' is-on' : ''}`, 'aria-pressed': String(on) },
      el('span', { class: 'wd-extra-top' }, el('span', { class: 'booking-extras-ring', 'aria-hidden': 'true' }, lineDrawing(KIND[k].drawing, 18)), ...(on ? [lineDrawing('tick', 18)] : [])),
      el('span', { class: 'wd-extra-words' }, el('strong', {}, KIND[k].title), el('span', {}, KIND[k].sub)))
    b.addEventListener('click', () => { if (kind !== k) { kind = k; if (k !== 'regular') days = null } changed() })
    return b
  }
  const number = (label: string, d: number | 'other'): HTMLElement => {
    const on = d === 'other' ? otherOpen : !otherOpen && days === d
    const b = el('button', { type: 'button', class: `often-num${on ? ' is-on' : ''}`, 'aria-pressed': String(on), 'aria-label': d === 'other' ? 'Other' : `Every ${label} weeks` }, label)
    b.addEventListener('click', () => { if (d === 'other') { otherOpen = !otherOpen; if (!otherOpen && days !== null && !QUICK.includes(days)) days = null } else { otherOpen = false; days = d } changed() })
    return b
  }
  const draw = (): void => {
    clear(node)
    node.append(el('p', { class: 'often-q' }, o.question ?? 'What kind of customer?'),
      el('div', { class: 'often-kinds', style: `grid-template-columns:repeat(${kinds.length}, minmax(0, 1fr))` }, ...kinds.map(tile)))
    if (kind !== 'regular') return
    node.append(el('p', { class: 'often-q' }, 'Every how many weeks?'),
      el('div', { class: 'often-nums' }, ...QUICK.map((d) => number(String(d / 7), d)), number('Other', 'other')))
    if (!otherOpen) return
    // Other: the round's longer choices as chips, then any number of weeks.
    const longer = cycleOptions().filter((x) => x.days && !QUICK.includes(x.days))
    const chips = el('div', { class: 'often-other' }, ...longer.map((x) => {
      const on = days === x.days
      const b = el('button', { type: 'button', class: `parking-chip${on ? ' is-on' : ''}`, 'aria-pressed': String(on) }, x.label.charAt(0).toUpperCase() + x.label.slice(1))
      b.addEventListener('click', () => { days = x.days; changed() })
      return b
    }))
    const weeks = el('input', { type: 'text', inputmode: 'numeric', class: 'often-weeks', 'aria-label': 'Every how many weeks', placeholder: '3' }) as HTMLInputElement
    if (days && !QUICK.includes(days) && !longer.some((x) => x.days === days) && days % 7 === 0) weeks.value = String(days / 7)
    const use = el('button', { type: 'button', class: 'btn-secondary often-use' }, 'Use')
    const own = (): void => { const n = Number(weeks.value); if (Number.isInteger(n) && n >= 1 && n <= 520) { days = n * 7; changed() } else weeks.focus() }
    use.addEventListener('click', own)
    weeks.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); own() } })
    // An odd number of days from before (an import) stays as it was, said in words.
    const odd = days && !longer.some((x) => x.days === days) && days % 7 !== 0 ? el('p', { class: 'muted' }, `Now: every ${cycleLabel(days, null)}`) : null
    node.append(chips, el('div', { class: 'often-own' }, el('span', {}, 'Or every'), weeks, el('span', {}, 'weeks'), use), ...(odd ? [odd] : []))
  }
  draw()
  return { node, value, kind: () => kind }
}
